import { connect as connectSocket } from 'js/middleware/channels/socket';

export function orgAnalysisTopic(nonce) {
  return `org_analysis:generate:${nonce}`;
}

export function randomNonce() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(
      12,
      16
    )}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

/**
 * Joins org_analysis:generate:{nonce} and resolves/rejects once a terminal
 * event arrives. Generate/regenerate publish generate_complete/error; a chat
 * turn publishes chat_complete/error on the same topic. A join always
 * replays whatever the server last cached for this nonce, so this is safe
 * to call even if the background work already finished.
 */
export function joinOrgAnalysisChannel(
  token,
  topic,
  handlers = {},
  joinPayload = {}
) {
  const socket = connectSocket(token);
  if (!socket) {
    return Promise.reject(new Error('Unable to connect'));
  }

  const channel = socket.channel(topic, joinPayload);
  let finished = false;
  const once = (fn) => (payload) => {
    if (!fn || finished) {
      return;
    }
    finished = true;
    try {
      fn(payload);
    } finally {
      channel.leave();
    }
  };

  const onComplete = once(handlers.onComplete);
  const onError = once(handlers.onError);
  const onProgress = (payload) => {
    if (!finished && handlers.onProgress) {
      handlers.onProgress(payload);
    }
  };

  if (handlers.onProgress) {
    channel.on('generate_progress', onProgress);
    channel.on('chat_progress', onProgress);
  }
  // Streamed output. Each event carries everything written so far, and the
  // server never caches them, so they never arrive in the join reply -- the
  // next one simply brings a late or reconnected client up to date.
  if (handlers.onStream) {
    channel.on('chat_stream', (payload) => {
      if (!finished) handlers.onStream(payload);
    });
  }
  if (handlers.onPartial) {
    channel.on('generate_partial', (payload) => {
      if (!finished) handlers.onPartial(payload);
    });
  }
  channel.on('generate_complete', onComplete);
  channel.on('chat_complete', onComplete);
  channel.on('generate_error', onError);
  channel.on('chat_error', onError);

  return new Promise((resolve, reject) => {
    channel
      .join()
      .receive('ok', (reply) => {
        if (
          reply?.event === 'generate_complete' ||
          reply?.event === 'chat_complete'
        ) {
          onComplete(reply.payload);
        } else if (
          reply?.event === 'generate_error' ||
          reply?.event === 'chat_error'
        ) {
          onError(reply.payload);
        } else if (
          (reply?.event === 'generate_progress' ||
            reply?.event === 'chat_progress') &&
          handlers.onProgress
        ) {
          handlers.onProgress(reply.payload);
        }
        resolve(channel);
      })
      .receive('error', (resp) => reject(resp))
      .receive('timeout', () => reject(new Error('Channel join timed out')));
  });
}
